<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70">
    <!-- Container Modal -->
    <div class="w-full max-w-3xl m-3 overflow-hidden transition-all duration-200 bg-gray-800 rounded-lg shadow-xl">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-600">
        <div class="flex items-center gap-3">
          <!-- Mutant Images -->
          <div class="flex gap-2">
            <img
              v-if="mutantData.normalFace"
              :src="mutantData.normalFace"
              alt="Normal Face"
              class="object-cover w-12 h-12 rounded-md border-2 border-indigo-400"
              @error="handleImageError"
            />
            <img
              v-if="mutantData.angryFace"
              :src="mutantData.angryFace"
              alt="Angry Face"
              class="object-cover w-12 h-12 rounded-md border-2 border-indigo-400"
              @error="handleImageError"
            />
          </div>
          <!-- Name & Rarity -->
          <div>
            <h1 class="text-lg font-semibold text-indigo-200">
              {{ mutantData.name }}
            </h1>
            <div v-if="mutantData.rarity" class="flex items-center gap-1 mt-1">
              <template v-for="i in mutantData.rarity" :key="i">
                <i class="text-xs fas fa-star text-yellow-400 sm:text-sm"></i>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="p-4">
        <div class="flex border-b border-gray-600">
          <button
            @click="activeTab = 'info'"
            class="px-3 py-2 text-xs font-medium transition-colors duration-200 sm:text-sm"
            :class="activeTab === 'info' ? 'text-indigo-200 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'"
          >
            ข้อมูล
          </button>
          <button
            @click="activeTab = 'items'"
            class="px-3 py-2 text-xs font-medium transition-colors duration-200 sm:text-sm"
            :class="activeTab === 'items' ? 'text-indigo-200 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'"
          >
            รายการไอเทม
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="px-4 py-4 space-y-4 overflow-y-auto max-h-96">
        <!-- Loading -->
        <div v-if="isLoading" class="flex items-center justify-center py-4">
          <div class="w-6 h-6 border-t-2 rounded-full animate-spin border-indigo-600"></div>
        </div>

        <!-- Tab: Info -->
        <div v-else-if="activeTab === 'info'">
          <!-- Main Image -->
          <div class="flex justify-center mb-4">
            <img
              v-if="mutantData.img"
              :src="mutantData.img"
              alt="Mutant Image"
              class="object-cover w-48 h-48 rounded-lg border-2 border-indigo-400"
              @error="handleImageError"
            />
          </div>

          <!-- รางวัลที่ได้รับเมื่อฆ่า -->
          <section class="mb-4">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">รางวัลเมื่อฆ่าสำเร็จ</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm sm:grid-cols-4">
              <div class="flex items-center gap-2 bg-gray-700 rounded-md px-3 py-2">
                <i class="fas fa-coins text-gray-200 text-sm"></i>
                <span class="text-indigo-200">เหรียญ: <strong>{{ mutantData.rewards?.silver || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2 bg-gray-700 rounded-md px-3 py-2">
                <i class="fas fa-g text-yellow-400 text-sm"></i>
                <span class="text-indigo-200">ทอง: <strong>{{ mutantData.rewards?.gold || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2 bg-gray-700 rounded-md px-3 py-2">
                <i class="fas fa-gem text-blue-400 text-sm"></i>
                <span class="text-indigo-200">เพชร: <strong>{{ mutantData.rewards?.diamond || 0 }}</strong></span>
              </div>
              <!-- ✅ เพิ่ม Renown เมื่อฆ่า -->
              <div class="flex items-center gap-2 bg-gray-700 rounded-md px-3 py-2">
                <i class="fas fa-trophy text-orange-400 text-sm"></i>
                <span class="text-indigo-200">ชื่อเสียง: <strong>{{ mutantData.rewards?.renownKill || 0 }}</strong></span>
              </div>
            </div>
          </section>

          <!-- ✅ รางวัลที่ได้รับเมื่อจับ (แสดงเฉพาะ canCatch) -->
          <section v-if="mutantData.canCatch" class="mb-4">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">รางวัลเมื่อจับสำเร็จ</h2>
            <div class="grid grid-cols-1 gap-3 text-xs sm:text-sm sm:grid-cols-2">
              <div class="flex items-center gap-2 bg-gray-700 rounded-md px-3 py-2">
                <i class="fas fa-trophy text-orange-400 text-sm"></i>
                <span class="text-indigo-200">ชื่อเสียง: <strong>{{ mutantData.rewards?.renownCatch || 0 }}</strong></span>
              </div>
            </div>
          </section>
        </div>

        <!-- Tab: Items -->
        <div v-else-if="activeTab === 'items'">
          <!-- Normal Drop Items -->
          <section>
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ไอเทมดรอปปกติ</h2>
            <div v-if="sortedItems.length === 0" class="text-xs text-center text-indigo-200 sm:text-sm">
              ไม่มีไอเทม
            </div>
            <div v-else class="space-y-3">
              <div
                v-for="(item, index) in sortedItems"
                :key="index"
                class="flex items-center gap-3 p-3 rounded-md bg-gray-700 hover:bg-gray-650 transition-colors"
              >
                <img
                  v-if="itemDetails[item.name]?.img"
                  :src="itemDetails[item.name].img"
                  alt="Item Image"
                  class="object-cover w-10 h-10 rounded-md border border-indigo-400"
                  @error="handleImageError"
                />
                <div v-else-if="isLoadingItem[item.name]" class="w-10 h-10 flex items-center justify-center">
                  <div class="w-4 h-4 border-t-2 rounded-full animate-spin border-indigo-600"></div>
                </div>

                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      <h3 class="text-xs font-semibold text-indigo-200 sm:text-sm">{{ item.name }}</h3>
                      <div v-if="item.transferable" title="สามารถโอนได้">
                        <i class="fa-solid fa-arrow-right-arrow-left text-xs text-green-500"></i>
                      </div>
                    </div>
                    <button
                      @click="openItemDetail(item.name)"
                      class="text-indigo-400 hover:text-indigo-300 transition-colors"
                      title="ดูรายละเอียดไอเทม"
                    >
                      <i class="fas fa-info-circle text-base"></i>
                    </button>
                  </div>
                  <div class="flex items-center gap-2 text-xs text-indigo-200 sm:text-sm mt-1">
                    <span>โอกาสดรอป: {{ (item.dropChance / 100).toFixed(2) }}%</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Catch Drop Items -->
          <section v-if="mutantData.canCatch && sortedCatchItems.length > 0">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2 mt-6">ไอเทมดรอปเมื่อจับสำเร็จ</h2>
            <div class="space-y-3">
              <div
                v-for="(item, index) in sortedCatchItems"
                :key="index"
                class="flex items-center gap-3 p-3 rounded-md bg-gray-700 hover:bg-gray-650 transition-colors"
              >
                <img
                  v-if="itemDetails[item.name]?.img"
                  :src="itemDetails[item.name].img"
                  alt="Item Image"
                  class="object-cover w-10 h-10 rounded-md border border-indigo-400"
                  @error="handleImageError"
                />
                <div v-else-if="isLoadingItem[item.name]" class="w-10 h-10 flex items-center justify-center">
                  <div class="w-4 h-4 border-t-2 rounded-full animate-spin border-indigo-600"></div>
                </div>

                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      <h3 class="text-xs font-semibold text-indigo-200 sm:text-sm">{{ item.name }}</h3>
                      <div v-if="item.transferable" title="สามารถโอนได้">
                        <i class="fa-solid fa-arrow-right-arrow-left text-xs text-green-500"></i>
                      </div>
                    </div>
                    <button
                      @click="openItemDetail(item.name)"
                      class="text-indigo-400 hover:text-indigo-300 transition-colors"
                      title="ดูรายละเอียดไอเทม"
                    >
                      <i class="fas fa-info-circle text-base"></i>
                    </button>
                  </div>
                  <div class="flex items-center gap-2 text-xs text-indigo-200 sm:text-sm mt-1">
                    <span>โอกาสดรอป: {{ (item.dropChance / 100).toFixed(2) }}%</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end p-4 border-t border-gray-600 gap-3">
        <button
          v-if="isAdmin"
          @click="$emit('editMutant', mutantId)"
          class="px-4 py-1 text-xs font-medium text-indigo-200 bg-indigo-600 rounded-md hover:bg-indigo-700 sm:text-sm"
        >
          แก้ไข
        </button>
        <button
          @click="$emit('cancelModal')"
          class="px-4 py-1 text-xs font-medium text-indigo-200 bg-indigo-600 rounded-md hover:bg-indigo-700 sm:text-sm"
        >
          ปิด
        </button>
      </div>
    </div>

    <!-- Modal รายละเอียดไอเทม -->
    <DetailItemModal
      v-if="showDetailModal"
      :item="selectedItem"
      @close="showDetailModal = false"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { usePlayerStore } from '@/composables/usePlayerStore';
import DetailItemModal from '@/components/admin/DetailItemModal.vue';
import gameItemsData from '@/data/gameItems.json';
import gameMutantsData from '@/data/gameMutants.json';

export default {
  components: {
    DetailItemModal
  },
  props: ['mutantId'],
  emits: ['cancelModal', 'editMutant'],
  setup(props, { emit }) {
    const playerStore = usePlayerStore();

    const mutantData = ref({});
    const isLoading = ref(true);
    const activeTab = ref('info');
    const itemDetails = ref({});
    const isLoadingItem = ref({});
    const isAdmin = ref(false);

    const showDetailModal = ref(false);
    const selectedItem = ref(null);

    const sortedItems = computed(() => {
      if (!mutantData.value.items) return [];
      return [...mutantData.value.items].sort((a, b) => b.dropChance - a.dropChance);
    });

    const sortedCatchItems = computed(() => {
      if (!mutantData.value.catchItems) return [];
      return [...mutantData.value.catchItems].sort((a, b) => b.dropChance - a.dropChance);
    });

    const catchChance = computed(() => {
      const diff = mutantData.value.catchDifficulty;
      return [null, 5, 4, 3, 2, 1][diff] || 5;
    });

    const openItemDetail = (itemName) => {
      if (!itemDetails.value[itemName]) {
        fetchItemDetails(itemName);
      }
      selectedItem.value = itemDetails.value[itemName] || { name: itemName };
      showDetailModal.value = true;
    };

    const fetchMutant = async () => {
      try {
        const data = gameMutantsData[props.mutantId];
        if (data) {
          mutantData.value = data;
          (mutantData.value.items || []).forEach(item => fetchItemDetails(item.name));
          (mutantData.value.catchItems || []).forEach(item => fetchItemDetails(item.name));
        } else {
          alert('ไม่พบข้อมูลสิ่งมีชีวิตกลายพันธุ์');
          emit('cancelModal');
        }
      } catch (error) {
        console.error('Error fetching mutant:', error);
        alert(`เกิดข้อผิดพลาด: ${error.message}`);
      } finally {
        isLoading.value = false;
      }
    };

    const fetchItemDetails = (itemName) => {
      if (itemDetails.value[itemName] || isLoadingItem.value[itemName]) return;
      try {
        isLoadingItem.value[itemName] = true;
        itemDetails.value[itemName] = gameItemsData[itemName] || null;
      } catch (error) {
        console.error(`Error fetching item ${itemName}:`, error);
        itemDetails.value[itemName] = null;
      } finally {
        isLoadingItem.value[itemName] = false;
      }
    };

    const fetchUserRole = () => {
      isAdmin.value = playerStore.userData?.admin === true;
    };

    const handleImageError = (event) => {
      event.target.src = 'https://via.placeholder.com/64?text=Image+Not+Found';
    };

    onMounted(() => {
      fetchUserRole();
      fetchMutant();
    });

    return {
      mutantData,
      isLoading,
      activeTab,
      sortedItems,
      sortedCatchItems,
      itemDetails,
      isLoadingItem,
      handleImageError,
      isAdmin,
      catchChance,
      showDetailModal,
      selectedItem,
      openItemDetail
    };
  }
};
</script>

<style scoped>
button:hover i.fa-info-circle {
  transform: scale(1.15);
}
</style>